/**
 * The running trend of each body composition series (#6627), built from readings already loaded.
 * Pure, so a unit test can drive it (`body-comp-trends.test.ts`); the loading is
 * `ChartDataBodyCompositionService.fetchTrendsAsync`.
 */

import { ProgressChartDataPoint } from 'components/cs-progress-chart/cs-progress-chart';
import { ProgressChartTrend } from 'components/cs-progress-chart/progress-chart-trend';
import { buildRunningTrendRows, RUNNING_TREND_BODY_FAT_OPTIONS, RUNNING_TREND_WEIGHT_OPTIONS, RunningTrendOptions } from '@cf-shared/running-trend';

/** the running trend options of each body composition series: 14 days for weight, 28 for skinfolds and waist */
export const TREND_OPTIONS_BY_SERIES: { readonly [seriesId: string]: RunningTrendOptions } = {
  weight: RUNNING_TREND_WEIGHT_OPTIONS,
  waist: RUNNING_TREND_BODY_FAT_OPTIONS,
  skinfold: RUNNING_TREND_BODY_FAT_OPTIONS,
};

/** one trend per series id, each from that series' readings and with that series' options */
export function buildBodyCompTrends(
  dataPoints: readonly ProgressChartDataPoint[],
  seriesIds: readonly string[],
  range: { firstDayIso: string; lastDayIso: string; todayIso: string },
  showTunnel: boolean,
): ProgressChartTrend[] {
  return seriesIds.map(seriesId => {
    const options = TREND_OPTIONS_BY_SERIES[seriesId];
    // a series without options is a mistake in the calling code; a guessed window would draw a wrong trend silently
    if (!options) {
      throw Error(`buildBodyCompTrends(): no running trend options for series "${seriesId}"`);
    }
    const readings = dataPoints
      .filter(p => typeof p[seriesId] === 'number')
      .map(p => ({ dateIso: p.date, value: p[seriesId] as number }));
    return { seriesId, rows: buildRunningTrendRows(readings, range, options), showTunnel };
  });
}
